const serialize = data => {
  return JSON.parse(JSON.stringify(data, (_key, value) => {
    return value === null ? undefined : value;
  }));
};
const mergeProduct = (lineItem, product) => {
  return {
    ...lineItem,
    ...product,
    variant: {
      ...lineItem?.variant,
      ...product?.variant
    }
  };
};
export const serializeMeta = config => ({
  meta: {
    i18n: {
      locale: config.locale,
      currency: config.currency
    },
    ownership: {
      tenant_id: config.tenantId,
      oms_id: config.omsId,
      store_id: config.storeId
    }
  }
});
export const getLineItemCoupon = lineItem => {
  const eligible = lineItem.adjustments?.find(adjustment => {
    return adjustment?.eligible && adjustment?.promotionCode?.value;
  });
  return eligible?.promotionCode?.value;
};
export const subscriptionLineItemToProduct = lineItem => {
  return {
    displayAmount: lineItem.displayPrice,
    price: lineItem.price,
    quantity: lineItem.quantity,
    variant: {
      description: lineItem.description,
      id: lineItem.variantId,
      images: lineItem.images,
      name: lineItem.name,
      price: lineItem.price,
      sku: lineItem.sku,
      slug: lineItem.productSlug
    },
    variantId: lineItem.variantId
  };
};
export const serializeLineItem = (config, lineItem, position, expandOptionValues) => {
  let lineItemId;
  const optionValues = lineItem.variant?.optionValues?.map(value => {
    if (!expandOptionValues) return value?.name;
    let optionTypeName;
    if ('optionTypeName' in value) {
      optionTypeName = value.optionTypeName;
    } else if ('optiontypeName' in value) {
      optionTypeName = value.optiontypeName;
    }
    return {
      name: `${value?.presentation} Option Value`,
      presentation: value?.presentation,
      slug: value?.name,
      option_type: [{
        slug: optionTypeName
      }]
    };
  });
  if ('id' in lineItem && lineItem.id) {
    lineItemId = lineItem.id.toString();
  }
  let url = '';
  try {
    const formatted = config.productUrlFormatter(lineItem);
    if (formatted) url = String(formatted);
  } catch (error) {
    console.error('productUrlFormatter error', error);
  }
  // Kits are bundles by the tracking plan definition and can be detected if the
  // sku contains a pipe character.
  let bundle = lineItem.variant?.sku.indexOf('|') >= 0;
  // If not a kit, check to see if bundle. Kits are not bundles by the OMS
  // defintion BUT are bundles by the tracking plan definition.
  if (!bundle && 'isBundle' in lineItem && typeof lineItem.isBundle === 'boolean') {
    bundle = lineItem.isBundle;
  }
  return {
    brand: lineItem.variant?.store?.slug || config.brandName,
    coupon: getLineItemCoupon(lineItem) || '',
    image_url: lineItem.variant?.images?.[0]?.largeUrl || lineItem.variant?.images?.[0]?.smallUrl || lineItem.variant?.images?.[0]?.miniUrl,
    name: lineItem.variant?.name,
    option_values: optionValues,
    bundle,
    description: lineItem.variant?.description,
    position,
    price: Number(lineItem.variant?.price),
    product_id: lineItem.variant?.slug,
    quantity: lineItem.quantity,
    sku: lineItem.variant?.sku,
    url,
    variant: lineItem.variant?.sku,
    line_item_id: lineItemId
  };
};
const serializeLineItems = (config, lineItems, products) => {
  return lineItems.map((lineItem, index) => {
    const product = products?.find(p => {
      return p.variant?.sku === lineItem.variant?.sku;
    });
    const data = product ? mergeProduct(lineItem, product) : lineItem;
    return serializeLineItem(config, data, index + 1);
  });
};
export const serializeCouponItem = (config, props) => {
  const {
    couponCode,
    order
  } = props;
  return {
    cart_id: order.number,
    coupon_id: couponCode,
    order_id: order.number,
    ...serializeMeta(config)
  };
};
export const serializeSubscription = (config, props) => {
  const {
    products,
    subscription
  } = props;
  const subscriptionProducts = subscription.lineItems.map((lineItem, index) => {
    const product = products?.find(p => {
      return p.variant?.sku === lineItem.sku;
    });
    const subscriptionLineItem = subscriptionLineItemToProduct(lineItem);
    const data = product ? mergeProduct(subscriptionLineItem, product) : subscriptionLineItem;
    return serializeLineItem(config, data, index + 1);
  });
  return serialize({
    id: subscription.id,
    brand: config.brandName,
    state: subscription.state,
    interval_units: subscription.intervalUnits,
    interval_length: subscription.intervalLength,
    end_date: subscription.endDate,
    actionable_date: subscription.actionableDate,
    products: subscriptionProducts,
    address: {
      name: subscription.shipAddress?.name,
      address1: subscription.shipAddress?.address1,
      address2: subscription.shipAddress?.address2,
      city: subscription.shipAddress?.city,
      country: subscription.shipAddress?.country.iso,
      state: subscription.shipAddress?.state.abbr,
      zipcode: subscription.shipAddress?.zipcode
    },
    ...serializeMeta(config)
  });
};
export const serializeCartViewed = (config, props) => {
  const {
    order,
    products
  } = props;
  return serialize({
    brand: order.store?.name,
    cart_id: order.number,
    products: serializeLineItems(config, order.lineItems, products),
    ...serializeMeta(config)
  });
};
export const serializeCheckoutCompleted = (config, props) => {
  const {
    order,
    products,
    trackingIds
  } = props;
  const couponAdjustment = props.order.lineItems.map(lineItem => lineItem.adjustments).flat().find(adjustment => adjustment.promotionCode);
  const serializedProducts = serializeLineItems(config, order.lineItems, products).map(product => {
    const lineItem = order.lineItems.find(i => i.variant?.sku === product.sku);
    return {
      ...product,
      name: lineItem.variant?.slug,
      price: lineItem.price,
      total: lineItem.total
    };
  });
  return serialize({
    value: parseFloat(order.total),
    currency: 'USD',
    transaction_id: order.number,
    discount: parseFloat(order.additionalTaxTotal) - parseFloat(order.adjustmentTotal),
    email_hash: order.email,
    store: order.store.code,
    tracking_ids: trackingIds ?? {},
    coupon: couponAdjustment?.promotionCode?.value,
    products: serializedProducts,
    ...serializeMeta(config)
  });
};
export const serializeCheckoutStarted = (config, props) => {
  const {
    order,
    products
  } = props;
  const couponAdjustment = order.lineItems.map(lineItem => lineItem.adjustments).flat().find(adjustment => adjustment.promotionCode);
  return serialize({
    affiliation: order.store?.name,
    order_id: order.number,
    currency: order.currency,
    revenue: parseFloat(order.total),
    discount: parseFloat(order.additionalTaxTotal) - parseFloat(order.adjustmentTotal),
    shipping: parseFloat(order.shipTotal),
    tax: parseFloat(order.taxTotal),
    total: parseFloat(order.total),
    coupon: couponAdjustment?.promotionCode?.value,
    products: serializeLineItems(config, order.lineItems, products),
    ...serializeMeta(config)
  });
};
export const serializeCollectionClicked = (config, props) => {
  const {
    collection
  } = props;
  return serialize({
    brand: collection.brand || config.brandName,
    collection_id: collection.collectionId,
    title: collection.title,
    url: collection.url,
    ...serializeMeta(config)
  });
};
export const serializeEmailCaptured = (config, props) => {
  const {
    emailCapture
  } = props;
  return serialize({
    email: emailCapture.email,
    web_placement: {
      component: emailCapture.component,
      page: emailCapture.page,
      website: emailCapture.website
    },
    ...serializeMeta(config)
  });
};
export const serializeOrderCompleted = (config, props) => {
  const {
    order,
    products,
    trackingIds
  } = props;
  const couponAdjustment = order.lineItems.map(lineItem => lineItem.adjustments).flat().find(adjustment => adjustment.promotionCode);
  const serializedProducts = serializeLineItems(config, order.lineItems, products).map(product => {
    const lineItem = order.lineItems.find(i => i.variant?.sku === product.sku);
    return {
      ...product,
      variant: lineItem.variant?.optionsText
    };
  });
  return serialize({
    affiliation: config.brandName,
    coupon: couponAdjustment?.promotionCode?.value,
    order_id: order.id,
    order_date: order.completedAt,
    total: parseFloat(order.total) || 0,
    shipping: parseFloat(order.shipTotal) || 0,
    tax: parseFloat(order.taxTotal) || 0,
    currency: 'USD',
    transaction_id: order.number,
    discount: parseFloat(order.additionalTaxTotal) - parseFloat(order.adjustmentTotal),
    email: order.email,
    email_hash: order.email,
    store: order.store.code,
    tracking_ids: trackingIds ?? {},
    revenue: parseFloat(order.total) || 0,
    products: serializedProducts,
    has_subscription: !!order.subscriptionInCart,
    is_recurring: false,
    ...serializeMeta(config)
  });
};
export const serializeProductAdded = (config, props) => {
  const {
    order,
    product,
    sku,
    quantity
  } = props;
  const lineItem = sku && order.lineItems.find(item => item.variant?.sku === sku);
  const data = product ? mergeProduct(lineItem, product) : lineItem;
  const serializedLineItem = serializeLineItem(config, data, undefined, true);
  return serialize({
    ...serializedLineItem,
    ...serializeMeta(config),
    quantity,
    cart_id: order.number
  });
};
export const serializeProductClicked = (config, props) => {
  const {
    order,
    product
  } = props;
  return serialize({
    ...serializeLineItem(config, product),
    ...serializeMeta(config),
    cart_id: order.number
  });
};
export const serializeProductRemoved = (config, props) => {
  const {
    order,
    product,
    sku,
    quantity
  } = props;
  const lineItem = sku && order.lineItems.find(item => item.variant?.sku === sku);
  const data = product ? mergeProduct(lineItem, product) : lineItem;
  const serializedLineItem = serializeLineItem(config, data, undefined, true);
  let subscriptionRelatedFields = {};
  if (lineItem?.subscriptionLineItems?.length) {
    const subscriptionLineItem = lineItem.subscriptionLineItems[0];
    subscriptionRelatedFields = {
      ...subscriptionRelatedFields,
      prepaid: false,
      interval_units: subscriptionLineItem.intervalUnits,
      interval_length: subscriptionLineItem.intervalLength
    };
  }
  return serialize({
    ...serializedLineItem,
    ...serializeMeta(config),
    ...subscriptionRelatedFields,
    quantity,
    cart_id: order.number
  });
};
export const serializeProductViewed = (config, props) => {
  const {
    order,
    product
  } = props;
  return serialize({
    ...serializeLineItem(config, product),
    ...serializeMeta(config),
    cart_id: order.number
  });
};
export const serializeStockRequestCreated = (config, props) => {
  const {
    order,
    product,
    email
  } = props;
  return serialize({
    ...serializeLineItem(config, product, undefined, true),
    ...serializeMeta(config),
    cart_id: order.number,
    email
  });
};
export const serializeVariantClicked = (config, props) => {
  const {
    order,
    product
  } = props;
  return serialize({
    ...serializeLineItem(config, product),
    ...serializeMeta(config),
    cart_id: order.number,
    variant: product.variant?.name,
    variant_id: product.variant?.sku
  });
};
export const serializeUnpurchasedSubscription = (config, props) => {
  const {
    order,
    lineItem,
    subscriptionLineItem,
    product
  } = props;
  const data = product ? mergeProduct(lineItem, product) : lineItem;
  const serializedLineItem = serializeLineItem(config, data, undefined, true);
  return {
    ...serializedLineItem,
    cart_id: order.number,
    quantity: subscriptionLineItem.quantity,
    sku: subscriptionLineItem.sku,
    actionable_date: new Date().toISOString(),
    end_date: subscriptionLineItem.endDate,
    image_url: subscriptionLineItem.images?.[0]?.largeUrl || subscriptionLineItem.images?.[0]?.smallUrl || subscriptionLineItem.images?.[0]?.miniUrl,
    interval_length: subscriptionLineItem.intervalLength,
    interval_units: subscriptionLineItem.intervalUnits,
    name: subscriptionLineItem.name,
    prepaid: false,
    price: Number(lineItem.price),
    ...serializeMeta(config)
  };
};
const addInstallmentIntervalToDate = _ref => {
  let {
    date: originalDate = new Date(),
    unit,
    length,
    installments
  } = _ref;
  const intervalFactor = length * installments;
  const date = new Date(originalDate.getTime());
  switch (unit) {
    case 'day':
      return new Date(date.setDate(date.getDate() + intervalFactor));
    case 'week':
      return new Date(date.setDate(date.getDate() + intervalFactor * 7));
    case 'month':
      return new Date(date.setMonth(date.getMonth() + intervalFactor));
    case 'year':
      return new Date(date.setFullYear(date.getFullYear() + intervalFactor));
    default:
      throw new Error(`unsupport unit provided (${unit})`);
  }
};
export const serializePrepaidSubscription = (config, props) => {
  const {
    order,
    lineItem,
    prepaidSubscription,
    product
  } = props;
  let actionableDate = new Date();
  if (typeof prepaidSubscription.actionableDate === 'string') {
    actionableDate = new Date(prepaidSubscription.actionableDate);
  } else if (prepaidSubscription.actionableDate) {
    actionableDate = prepaidSubscription.actionableDate;
  }
  const endDate = addInstallmentIntervalToDate({
    date: actionableDate,
    unit: prepaidSubscription.intervalUnits,
    length: prepaidSubscription.intervalLength,
    installments: prepaidSubscription.installmentCount
  });
  const data = product ? mergeProduct(lineItem, product) : lineItem;
  const serializedLineItem = serializeLineItem(config, data, undefined, true);
  return {
    ...serializedLineItem,
    cart_id: order.number,
    quantity: prepaidSubscription.quantity,
    actionable_date: actionableDate.toISOString(),
    end_date: endDate.toISOString(),
    image_url: lineItem.variant.images?.[0]?.largeUrl || lineItem.variant.images?.[0]?.smallUrl || lineItem.variant.images?.[0]?.miniUrl,
    installment_count: prepaidSubscription.installmentCount,
    interval_length: prepaidSubscription.intervalLength,
    interval_units: prepaidSubscription.intervalUnits,
    prepaid: true,
    price: Number(lineItem.price),
    ...serializeMeta(config)
  };
};